import React from "react";
import {
    Box,
    Typography,
    IconButton,
    Collapse,
    Checkbox,
    FormControlLabel,
    Stack,
    styled
} from "@mui/material";
import CustomIcon from "../../../Components/CustomIcon";


const NodeItem = styled(Stack)(({ selected, highlight, isLeaf, onClick }) => ({
    backgroundColor: selected ? "#EFF0FF" : "inherit",
    borderRadius: 4,
    cursor: onClick ? "pointer" : "auto",
    "&:hover": {
        backgroundColor: "#F9F9F9",
    },
    "& .icon-button": {
        width: 24,
        height: 24,
        background: "none",
    },
    "& .MuiTypography-root": {
        color: (selected || highlight) && !isLeaf ? "#4D44D8" : "inherit",
        fontWeight: selected ? 500 : "inherit",
    },
}));

const ItemRowCheckBoxLabel = styled(FormControlLabel)(() => ({
    display: 'block',
    marginLeft: '0px',
    '& .MuiCheckbox-root': {
        padding: 0,
        marginBottom: '8px',
        marginRight: '8px',
        marginTop: '8px',
    },
    '& .MuiCheckbox-root.Mui-checked': {
        color: '#494949 !important',
    },
    '& .MuiCheckbox-root.Mui-disabled': {
        color: '#989898 !important',
    },
}));

const BatteryTreeNode = ({
    node,
    level,
    maxLevel,
    openNodes,
    setOpenNodes,
    selectedIds,
    setSelectedIds,
    onChangeNode,
    shouldDisableCheckbox,
}) => {
    const hasChildren = node.children?.length > 0;
    const isOpen = openNodes[node.id] || false;
    const isLeaf = !hasChildren;
    const isCheckboxLevel = level === maxLevel;

    const childrenAtMaxLevel = (node.children || []).filter(child => level + 1 === maxLevel);
    const childIds = childrenAtMaxLevel.map(child => child.id);
    const isAllSelected = childIds.every(id => selectedIds.includes(id));
    const isPartiallySelected = childIds.some(id => selectedIds.includes(id)) && !isAllSelected;

    const handleToggle = (event) => {
        event.stopPropagation();
        setOpenNodes(prev => ({
            ...prev,
            [node.id]: !prev[node.id],
        }));
    };

    const toggleCheckbox = (id) => {
        const updatedIds = selectedIds.includes(id)
            ? selectedIds.filter(sid => sid !== id)
            : [...selectedIds, id];
        setSelectedIds(updatedIds);
        onChangeNode(updatedIds);
    };

    const handleLocalSelectAll = (e) => {
        const checked = e.target.checked;
        const updated = checked
            ? Array.from(new Set([...selectedIds, ...childIds]))
            : selectedIds.filter(id => !childIds.includes(id));
        setSelectedIds(updated);
        onChangeNode(updated);
    };

    return (
        <Box key={node.id} ml={level * 2.5} mt={0.5} className="node-item">
            <NodeItem direction="row" alignItems="center" isLeaf={isLeaf}>
                {hasChildren && level < maxLevel ? (
                    <>
                        <IconButton className="icon-button" onClick={handleToggle}>
                            <CustomIcon name="primary-arrow-icon" rotate={isOpen ? 90 : 0} />
                        </IconButton>
                        <Typography variant="fw400s12c8F8F8F">{node.label}</Typography>
                    </>
                ) : isCheckboxLevel ? (
                    <ItemRowCheckBoxLabel
                        key={node.id}
                        control={
                            <Checkbox
                                sx={{ color: '#494949' }}
                                checked={selectedIds.includes(node.id)}
                                onChange={() => toggleCheckbox(node.id)}
                                disabled={shouldDisableCheckbox}
                            />
                        }
                        label={<Typography variant={'fw400s12c494949'}>{node.label}</Typography>}
                    />
                ) : (
                    <Box sx={{ display: "flex", alignItems: "center", borderLeft: "1px solid #BBBBBB", mt: -0.5 }} className="last-view">
                        <Box className="center-update" sx={{ position: "relative", left: "-0.79px", bottom: "4px" }}>
                            <CustomIcon color="#BBBBBB" height="50px" name="last-node-level-icon" />
                        </Box>
                        <Typography variant="fw400s12c494949">{node.label}</Typography>
                    </Box>
                )}
            </NodeItem>

            {hasChildren && isOpen && level < maxLevel && (
                <Collapse in={isOpen} timeout="auto" unmountOnExit>
                    <Box className="node-children-container">
                        {childrenAtMaxLevel.length > 0 && (
                            <ItemRowCheckBoxLabel
                                sx={{ paddingLeft: `${(level + 1) * 20}px`, mt: 0.5 }}
                                key={`${node.id}_select_all`}
                                control={
                                    <Checkbox
                                        sx={{ color: '#494949' }}
                                        checked={isAllSelected}
                                        indeterminate={isPartiallySelected}
                                        onChange={handleLocalSelectAll}
                                    />
                                }
                                label={<Typography variant={'fw500s12lh24c494949'}> All </Typography>}
                            />
                        )}
                        {node.children.map(child => (
                            <BatteryTreeNode
                                key={child.id}
                                node={child}
                                level={level + 1}
                                maxLevel={maxLevel}
                                openNodes={openNodes}
                                setOpenNodes={setOpenNodes}
                                selectedIds={selectedIds}
                                setSelectedIds={setSelectedIds}
                                onChangeNode={onChangeNode}
                            />
                        ))}
                    </Box>
                </Collapse>
            )}
        </Box>
    );
};

export default BatteryTreeNode;